import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { User, Plus, X } from "lucide-react";
import { useProjectMembers } from "@/hooks/useProjectMembers";
import { fetchClient } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import AddProjectMembersModal from "../AddProjectMembersModal";

interface TeamTabProps {
  projectId: string;
  project: any;
  onProjectUpdate?: (project: any) => void;
}

export default function TeamTab({ projectId, project, onProjectUpdate }: TeamTabProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);

  const { data: members = [], isLoading: loadingMembers } = useProjectMembers(projectId);

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm("Remove this member from the project?")) return;
    try {
      const { error: deleteError } = await fetchClient.DELETE(
        "/api/v1/projects/{project_id}/members/{member_id}",
        { params: { path: { project_id: projectId, member_id: memberId } } },
      );
      if (deleteError) return;
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/v1/projects/{project_id}/members"],
      });
    } catch {
      // Silently fail -- query will show stale data
    }
  };

  const isOwner = project?.owner?.id === user?.id || project?.owner_id === user?.id;

  return (
    <div className="p-6 space-y-8 overflow-auto h-full">
      {/* Project Members */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>Team Members</h2>
          <button
            onClick={() => setIsAddMembersModalOpen(true)}
            className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-[var(--accent-hover)] transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Member
          </button>
        </div>

        {loadingMembers ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="rounded-xl p-4 animate-pulse"
                style={{ borderWidth: "1px", borderStyle: "solid", borderColor: "var(--border)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full" style={{ backgroundColor: "var(--border)" }} />
                  <div className="flex-1">
                    <div className="h-4 rounded w-1/2 mb-2" style={{ backgroundColor: "var(--border)" }} />
                    <div className="h-3 rounded w-1/3" style={{ backgroundColor: "var(--border)" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="empty-state">
            <User className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--muted-foreground)" }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No team members added yet</p>
            <button
              onClick={() => setIsAddMembersModalOpen(true)}
              className="mt-4 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-[var(--accent-hover)] transition-colors"
            >
              Add First Member
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {members.map((member) => (
              <div
                key={member.id}
                className="card-base card-interactive rounded-xl p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full text-sm font-medium flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--purple)", color: "white" }}>
                      {(member.display_name || member.email || "U")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                        {member.display_name || member.email}
                      </div>
                      {member.role && (
                        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{member.role}</div>
                      )}
                      {member.email && member.display_name && (
                        <div className="text-xs truncate" style={{ color: "var(--muted-foreground)" }}>{member.email}</div>
                      )}
                    </div>
                  </div>
                  {isOwner && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMember(member.id);
                      }}
                      className="p-1 rounded transition-colors"
                      style={{ color: "var(--error)" }}
                      title="Remove member"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isAddMembersModalOpen && (
        <AddProjectMembersModal
          isOpen={isAddMembersModalOpen}
          onClose={() => setIsAddMembersModalOpen(false)}
          projectId={projectId}
          projectName={project?.name || "Project"}
          projectTenantId={project?.tenant_id}
          existingMembers={members.map((m) => ({
            id: m.id,
            email: m.email || "",
            display_name: m.display_name || "",
            first_name: "",
            last_name: "",
          }))}
          onMembersChanged={async () => {
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/v1/projects/{project_id}/members"],
            });
          }}
        />
      )}
    </div>
  );
}
